import { useEffect, useRef } from 'react';
import {
  getOrCreateExtensionDeviceId,
  isExtensionAutoAuthEnabled,
} from '../config/extensionTokensFlags';
import { getExtensionSession } from '../services/extensionTokens.api';
import {
  getExtensionId,
  getExtensionStatus,
  sendIngestTokenToExtension,
} from '../services/extensionHandshake';

/**
 * Treat a token as healthy until it is actually dead. Rotation belongs to the
 * extension's own refresh alarm (AD-EA-2, single rotation authority) — the
 * website must not preempt it, or an in-flight refresh can 401 and disconnect
 * a working extension.
 */
export const EXPIRY_SKEW_MS = 30 * 1000;

/** Transient failures (SW asleep, Strict Mode remount, brief 5xx) get a few tries. */
const MAX_ATTEMPTS = 3;

/**
 * Decide what the website should do about the extension's session.
 * Pure — the whole point is that "skip" is the common answer.
 *
 * @param {{ ok: boolean, connected: boolean, expiresAt: string|null }} status
 * @returns {{ action: 'skip'|'provision', reason: string }}
 */
export function planExtensionAutoConnect(status) {
  if (!status?.ok) {
    // No extension installed, or it never answered — nothing to provision.
    return { action: 'skip', reason: status?.reason || 'extension_unreachable' };
  }
  if (!status.connected) {
    return { action: 'provision', reason: 'not_connected' };
  }
  const expiresAtMs = status.expiresAt ? Date.parse(status.expiresAt) : NaN;
  if (!Number.isFinite(expiresAtMs)) {
    return { action: 'provision', reason: 'unknown_expiry' };
  }
  if (expiresAtMs <= Date.now() + EXPIRY_SKEW_MS) {
    return { action: 'provision', reason: 'expired' };
  }
  return { action: 'skip', reason: 'healthy' };
}

/**
 * Hand the extension an Extension Token without the student doing anything.
 *
 * The website holds the session; the extension holds only its Extension Token
 * and never sees a website JWT (AD-EA-1). Silent on every failure path — this
 * runs for students who may not have the extension installed at all.
 *
 * Important: do not latch "attempted" before the async work finishes. React 18
 * Strict Mode (and any effect remount) runs setup→cleanup→setup; latching early
 * + cancelling the in-flight call left the extension disconnected for the whole
 * signed-in SPA session with no retry.
 *
 * @param {{ enabled: boolean, onProvisioned?: () => void }} params
 */
export default function useExtensionAutoConnect({ enabled, onProvisioned }) {
  /** True only after a terminal outcome for this signed-in session (healthy or pushed). */
  const doneRef = useRef(false);
  // Kept in a ref so a changing callback identity cannot re-run the effect and
  // re-trigger provisioning.
  const onProvisionedRef = useRef(onProvisioned);
  onProvisionedRef.current = onProvisioned;

  useEffect(() => {
    // Reset on sign-out so a subsequent sign-in re-provisions in the same tab.
    if (!enabled) {
      doneRef.current = false;
      return undefined;
    }
    if (doneRef.current) return undefined;
    if (!isExtensionAutoAuthEnabled()) return undefined;
    if (!getExtensionId()) {
      // Misconfiguration, not a user error: the manual panel still works.
      console.warn('[stacktrack] auto-connect disabled — REACT_APP_EXTENSION_ID is not set');
      return undefined;
    }

    let cancelled = false;
    let retryTimer = null;

    const scheduleRetry = (attempt) => {
      if (cancelled || attempt >= MAX_ATTEMPTS) return;
      const delayMs = 750 * attempt;
      retryTimer = setTimeout(() => {
        retryTimer = null;
        void run(attempt + 1);
      }, delayMs);
    };

    const run = async (attempt) => {
      try {
        const status = await getExtensionStatus();
        if (cancelled) return;

        const plan = planExtensionAutoConnect(status);
        if (plan.action === 'skip') {
          if (plan.reason === 'healthy') {
            doneRef.current = true;
            return;
          }
          // Extension unreachable / timed out — SW may still be waking. Retry.
          scheduleRetry(attempt);
          return;
        }

        // AD-EA-3: the extension's device id wins; the localStorage id is only
        // a seed for a fresh install that has never held a token.
        const deviceId = status.deviceId || getOrCreateExtensionDeviceId();
        const data = await getExtensionSession({
          deviceId,
          jti: plan.reason === 'expired' ? null : status.jti,
          tokenVersion: status.tokenVersion,
        });
        if (cancelled) return;

        if (data?.reused || !data?.token) {
          // Backend confirmed an existing grant (or issued nothing). Stop looping.
          doneRef.current = true;
          return;
        }

        const push = await sendIngestTokenToExtension({
          token: data.token,
          expiresAt: data.expiresAt,
          deviceId: data.deviceId,
          jti: data.jti,
        });
        if (cancelled) return;

        if (push?.ok) {
          doneRef.current = true;
          onProvisionedRef.current?.();
          return;
        }

        scheduleRetry(attempt);
      } catch (err) {
        // 401 (website session gone), 404 (flag off), offline — retry a few times
        // then stay silent; the manual Connect panel remains the fallback.
        console.debug('[stacktrack] extension auto-connect skipped', err?.message || err);
        if (!cancelled) scheduleRetry(attempt);
      }
    };

    void run(1);

    return () => {
      cancelled = true;
      if (retryTimer != null) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      // If cleanup interrupted us before a terminal outcome, the next setup
      // (Strict Mode remount, brief enabled flicker) must be allowed to try again.
    };
  }, [enabled]);
}
